import type { PeerId } from '@live-stream/shared'
import {
  applyProfile,
  applyScreenProfile,
  describePath,
  preferScreenCodecs,
  scaleFactorFor,
  ScreenAdapter,
  type CandidateLike,
  type LimitationReason,
  type ScreenProfile,
  type SendProfile,
} from './encoding.js'
import type { ConnectionState, PeerStats, TrackKind } from './transport.js'

/**
 * One peer connection, implementing **perfect negotiation**.
 *
 * https://w3c.github.io/webrtc-pc/#perfect-negotiation-example
 *
 * Ad-hoc offer/answer handling works right up until both sides renegotiate at
 * the same moment — a device switch here, a screen share there — and then you
 * get glare: two offers cross, both peers reject the other's, and the call
 * wedges in `have-local-offer` forever. It is miserable to debug after the
 * fact because it is timing-dependent and never reproduces on demand.
 *
 * The pattern needs exactly three things:
 *   1. A deterministic, symmetric role assignment (polite vs impolite).
 *   2. The polite peer rolls back its own offer on collision.
 *   3. The impolite peer ignores the incoming offer on collision.
 *
 * Both sides compute the same roles from the same two ids, with no extra
 * signaling round trip.
 */

/** Which of our m-sections carries what, by mid. Travels with every description we send. */
export type TrackLabels = Record<string, TrackKind>

export interface PeerSignals {
  sendDescription(to: PeerId, sdp: RTCSessionDescriptionInit, tracks: TrackLabels): void
  sendCandidate(to: PeerId, candidate: RTCIceCandidateInit): void
}

export interface PeerEvents {
  onTrack(peerId: PeerId, kind: TrackKind, track: MediaStreamTrack, stream: MediaStream): void
  onTrackEnded(peerId: PeerId, trackId: string, kind: TrackKind): void
  onState(peerId: PeerId, state: ConnectionState): void
  onError(peerId: PeerId, code: string, message: string): void
}

export interface PeerOptions {
  peerId: PeerId
  localPeerId: PeerId
  iceServers: RTCIceServer[]
  signals: PeerSignals
  events: PeerEvents
  /** Force relay-only, to prove the TURN path works. */
  relayOnly?: boolean
}

/** Transceivers are created up front in a fixed order so `mid` values are stable. */
const TRANSCEIVER_ORDER: Array<'audio' | 'video'> = ['audio', 'video']

type ScreenKind = 'screen' | 'screen-audio'
const isScreenKind = (kind: TrackKind): kind is ScreenKind =>
  kind === 'screen' || kind === 'screen-audio'

export class Peer {
  readonly peerId: PeerId
  private readonly pc: RTCPeerConnection
  private readonly signals: PeerSignals
  private readonly events: PeerEvents

  /**
   * Deterministic and symmetric: the lexicographically smaller id is polite.
   * Both sides reach the same answer without negotiating who yields.
   */
  private readonly polite: boolean

  private makingOffer = false
  private ignoreOffer = false
  private isSettingRemoteAnswerPending = false
  private closed = false

  /** Senders by kind, so a camera swap is a replaceTrack rather than a renegotiation. */
  private readonly senders = new Map<TrackKind, RTCRtpSender>()
  private restartAttempts = 0
  /** The camera encoding in force toward this peer; re-applied whenever the camera track changes. */
  private cameraProfile: SendProfile | null = null
  /** The screen encoding in force toward this peer; applied when a share starts and when the hint changes. */
  private screenProfile: ScreenProfile | null = null
  /** Steps the share's resolution down while this link is short of bandwidth or CPU, and back up after. */
  private readonly screenAdapter = new ScreenAdapter()

  /**
   * The two transceivers every connection is born with: the microphone's
   * and the camera's, in that order. They are OURS to send on; they say
   * nothing about what arrives. A transceiver created by addTransceiver is
   * never reused for an incoming m-section, so on the side that answered
   * the first offer, the other party's camera lands on a transceiver the
   * offer created, and our two sit unassociated until we next offer.
   */
  private base!: { audio: RTCRtpTransceiver; video: RTCRtpTransceiver }
  /** Our own share toward this peer: a fresh send-only transceiver per share, stopped when it ends. */
  private readonly screenTransceivers = new Map<ScreenKind, RTCRtpTransceiver>()
  /**
   * What the other side says each of ITS m-sections carries, by mid — the
   * label that tells a screen from a camera on the way in, since SDP has
   * none (plan Phase 6). Filed from every description before it is applied,
   * so the track event that follows can look it up.
   */
  private readonly remoteKinds = new Map<string, TrackKind>()

  /**
   * Every track this peer sends us, in ONE stream, whatever the SDP said.
   *
   * Our senders attach tracks with replaceTrack on transceivers created up
   * front, and those carry no stream association — so on the receiving side
   * `event.streams` is empty and each track would land in a MediaStream of
   * its own. Anyone rendering by peer then keeps whichever stream arrived
   * last: audio comes first in the transceiver order, video second, and the
   * audio quietly vanishes. Assembling the stream here holds regardless of
   * browser, and regardless of how an SFU chooses to label its tracks later.
   */
  private readonly remoteStream = new MediaStream()
  /**
   * Their share, in a stream of its own. A <video> plays one video track —
   * the first — so a screen track dropped into the camera's stream would
   * either hide behind the camera or replace it. Two streams, two tiles.
   */
  private readonly remoteScreenStream = new MediaStream()

  constructor(opts: PeerOptions) {
    this.peerId = opts.peerId
    this.signals = opts.signals
    this.events = opts.events
    this.polite = opts.localPeerId < opts.peerId

    this.pc = new RTCPeerConnection({
      iceServers: opts.iceServers,
      // One transport for everything. Fewer ports, fewer NAT bindings, and
      // required by every SFU we might move to.
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceCandidatePoolSize: 2,
      ...(opts.relayOnly ? { iceTransportPolicy: 'relay' as const } : {}),
    })

    this.wire()
  }

  private wire(): void {
    // Create transceivers up front, in a fixed order, rather than letting
    // addTrack decide. Stable `mid` values make renegotiation and the Phase 7
    // SFU migration far less fiddly.
    const [audio, video] = TRANSCEIVER_ORDER.map((kind) =>
      this.pc.addTransceiver(kind, { direction: 'sendrecv' }),
    ) as [RTCRtpTransceiver, RTCRtpTransceiver]
    this.base = { audio, video }

    this.pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true
        // The parameterless form picks the right description for the current
        // signaling state, including after a rollback.
        await this.pc.setLocalDescription()
        if (this.pc.localDescription) {
          this.signals.sendDescription(
            this.peerId,
            this.pc.localDescription.toJSON(),
            this.labels(),
          )
        }
      } catch (err) {
        this.events.onError(this.peerId, 'negotiation_failed', String(err))
      } finally {
        this.makingOffer = false
      }
    }

    this.pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.signals.sendCandidate(this.peerId, candidate.toJSON())
    }

    this.pc.ontrack = ({ track, transceiver }) => {
      const kind = this.classify(track, transceiver)
      const stream = isScreenKind(kind) ? this.remoteScreenStream : this.remoteStream
      if (!stream.getTrackById(track.id)) stream.addTrack(track)
      if (kind === 'screen' && transceiver) preferLowDelay(transceiver.receiver)
      this.events.onTrack(this.peerId, kind, track, stream)
      // A share ends by its transceiver being stopped, which ends this track
      // on our side once the offer arrives; a camera track lives as long as
      // the connection (a camera turned off is a null sender, not an end).
      track.addEventListener('ended', () => {
        stream.removeTrack(track)
        this.events.onTrackEnded(this.peerId, track.id, kind)
      })
    }

    this.pc.onconnectionstatechange = () => {
      const state = this.currentState()
      if (state === 'connected') {
        this.restartAttempts = 0
        // Browsers that expose no encodings until negotiation completes get
        // the profile now (applyCameraProfile skipped them earlier).
        void this.applyCameraProfile()
        void this.applyScreenProfile()
      }
      // 'new' is reported, not skipped: without it a peer that exists but has
      // not finished offer/answer looks identical to no peer at all.
      this.events.onState(this.peerId, state)
      if (state === 'failed') void this.restartIce()
    }
  }

  /**
   * What an incoming track is: whatever the other side labelled the mid it
   * arrived on. A mid they did not label — an older client, a description
   * that crossed in flight — is taken for the camera or the microphone by
   * its media kind, which is what it was before shares existed.
   */
  private classify(track: MediaStreamTrack, transceiver: RTCRtpTransceiver | undefined): TrackKind {
    const mid = transceiver?.mid
    const labelled = mid !== null && mid !== undefined ? this.remoteKinds.get(mid) : undefined
    if (labelled) return labelled
    return track.kind === 'audio' ? 'microphone' : 'camera'
  }

  /** Our own labels, for every m-section of ours that has a mid yet. */
  private labels(): TrackLabels {
    const out: TrackLabels = {}
    if (this.base.audio.mid !== null) out[this.base.audio.mid] = 'microphone'
    if (this.base.video.mid !== null) out[this.base.video.mid] = 'camera'
    for (const [kind, transceiver] of this.screenTransceivers) {
      if (transceiver.mid !== null) out[transceiver.mid] = kind
    }
    return out
  }

  /** RTCPeerConnectionState in our vocabulary. */
  private currentState(): ConnectionState {
    switch (this.pc.connectionState) {
      case 'connected':
        return 'connected'
      case 'connecting':
        return 'connecting'
      case 'disconnected':
        // Often transient — a Wi-Fi blip recovers on its own within seconds.
        // Restarting ICE immediately would make a recoverable hiccup worse.
        return 'reconnecting'
      case 'failed':
        return 'failed'
      case 'closed':
        return 'closed'
      default:
        return 'new'
    }
  }

  /** The three-line core of perfect negotiation. */
  async handleDescription(sdp: RTCSessionDescriptionInit, tracks?: TrackLabels): Promise<void> {
    if (this.closed) return
    // Labels first: the track events fire inside setRemoteDescription.
    if (tracks) for (const [mid, kind] of Object.entries(tracks)) this.remoteKinds.set(mid, kind)

    try {
      const readyForOffer =
        !this.makingOffer &&
        (this.pc.signalingState === 'stable' || this.isSettingRemoteAnswerPending)
      const offerCollision = sdp.type === 'offer' && !readyForOffer

      this.ignoreOffer = !this.polite && offerCollision
      if (this.ignoreOffer) return

      this.isSettingRemoteAnswerPending = sdp.type === 'answer'
      await this.pc.setRemoteDescription(sdp)
      this.isSettingRemoteAnswerPending = false

      if (sdp.type === 'offer') {
        await this.pc.setLocalDescription()
        if (this.pc.localDescription) {
          this.signals.sendDescription(
            this.peerId,
            this.pc.localDescription.toJSON(),
            this.labels(),
          )
        }
      }
    } catch (err) {
      this.events.onError(this.peerId, 'sdp_failed', String(err))
    }
  }

  async handleCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.closed) return
    try {
      await this.pc.addIceCandidate(candidate)
    } catch (err) {
      // Candidates that arrive for an offer we deliberately ignored are
      // expected to fail. Anything else is worth surfacing.
      if (!this.ignoreOffer) this.events.onError(this.peerId, 'ice_failed', String(err))
    }
  }

  /** Publish or swap a local track without tearing anything down. */
  async setTrack(kind: TrackKind, track: MediaStreamTrack | null): Promise<void> {
    if (isScreenKind(kind)) return this.setScreenTrack(kind, track)

    // The camera and the microphone ALWAYS ride the transceivers created at
    // birth — never a spare one that happens to be free. A recvonly
    // transceiver the other side added for their share has a null sender
    // too, and a camera that landed on it would arrive over there labelled
    // as a screen.
    const transceiver = kind === 'microphone' ? this.base.audio : this.base.video
    // replaceTrack does NOT trigger renegotiation, so toggling a camera or
    // switching a microphone costs nothing on the wire.
    await transceiver.sender.replaceTrack(track)
    this.senders.set(kind, transceiver.sender)

    // A new camera track may capture at a different size (a device switch, a
    // phone rotating); the downscale factor is computed from the track.
    if (kind === 'camera' && track) await this.applyCameraProfile()
  }

  /**
   * A share is its own transceiver — never replaceTrack on the camera's:
   * camera and screen are independent tracks with independent lifetimes
   * and opposite encoder settings, and the camera must keep flowing while
   * a share starts and stops. Send-only, created fresh for each share, and
   * STOPPED when the share ends rather than emptied: a stopped transceiver
   * is rejected in the next offer and the other side's track ends, which
   * is the clean signal their tile is waiting for. Mids only ever grow.
   */
  private async setScreenTrack(kind: ScreenKind, track: MediaStreamTrack | null): Promise<void> {
    const existing = this.screenTransceivers.get(kind)
    if (track) {
      if (existing && !stopped(existing)) {
        await existing.sender.replaceTrack(track)
      } else {
        const transceiver = this.pc.addTransceiver(track, { direction: 'sendonly' })
        this.screenTransceivers.set(kind, transceiver)
        this.senders.set(kind, transceiver.sender)
        if (kind === 'screen') {
          // Before the offer that carries it: VP9 first, for its screen-content
          // tools. A peer without it negotiates the next in the list.
          preferCodecs(transceiver)
          this.screenAdapter.reset()
        }
      }
      if (kind === 'screen') await this.applyScreenProfile()
      return
    }
    if (!existing) return
    this.screenTransceivers.delete(kind)
    this.senders.delete(kind)
    try {
      existing.stop()
    } catch {
      // Already stopped by a closed connection; nothing to undo.
    }
  }

  /**
   * How our screen is encoded toward this peer: a bitrate cap, a frame cap
   * and what to give up first. Applied now if a screen sender exists, and
   * again when one appears.
   */
  async setScreenProfile(profile: ScreenProfile): Promise<void> {
    this.screenProfile = profile
    await this.applyScreenProfile()
  }

  private async applyScreenProfile(): Promise<void> {
    const sender = this.senders.get('screen')
    const profile = this.screenProfile
    if (!sender || !profile || this.closed) return
    const params = sender.getParameters()
    if (!applyScreenProfile(params, profile, this.screenAdapter.scale)) return
    try {
      await sender.setParameters(params)
    } catch (err) {
      this.events.onError(this.peerId, 'encoding_failed', String(err))
    }
  }

  /**
   * How the camera is encoded toward this peer: size, frame rate, bitrate
   * cap. Sender parameters only — the capture is untouched, so the local
   * preview stays sharp and nothing re-opens the camera. Applied now if a
   * camera sender exists, and again whenever the camera track changes.
   */
  async setCameraProfile(profile: SendProfile): Promise<void> {
    this.cameraProfile = profile
    await this.applyCameraProfile()
  }

  private async applyCameraProfile(): Promise<void> {
    const sender = this.senders.get('camera')
    const profile = this.cameraProfile
    if (!sender || !profile || this.closed) return

    // getParameters → setParameters must be back to back: the parameters
    // carry a transaction id that a second getParameters invalidates.
    const params = sender.getParameters()
    const factor = scaleFactorFor(sender.track?.getSettings(), profile)
    if (!applyProfile(params.encodings, profile, factor)) return
    try {
      await sender.setParameters(params)
    } catch (err) {
      // Degraded, not broken: this link runs at the browser's defaults.
      this.events.onError(this.peerId, 'encoding_failed', String(err))
    }
  }

  async restartIce(): Promise<void> {
    if (this.closed || this.restartAttempts >= 3) return
    this.restartAttempts++

    try {
      this.pc.restartIce()
      // restartIce only flags intent; an offer still has to be produced, and
      // onnegotiationneeded is not guaranteed to fire on every browser.
      await this.pc.setLocalDescription()
      if (this.pc.localDescription) {
        this.signals.sendDescription(this.peerId, this.pc.localDescription.toJSON(), this.labels())
      }
    } catch (err) {
      this.events.onError(this.peerId, 'ice_restart_failed', String(err))
    }
  }

  async stats(): Promise<PeerStats> {
    const out: PeerStats = {
      peerId: this.peerId,
      connectionState: this.currentState(),
      roundTripTimeMs: null,
      packetsReceived: 0,
      packetsLost: 0,
      packetsLostRatio: 0,
      jitterMs: 0,
      framesPerSecond: null,
      frameWidth: null,
      frameHeight: null,
      freezeCount: 0,
      qualityLimitationReason: 'none',
      sentWidth: null,
      sentHeight: null,
      sentFramesPerSecond: null,
      screenSentWidth: null,
      screenSentHeight: null,
      screenSentFramesPerSecond: null,
      screenBytesSent: null,
      screenFramesEncoded: null,
      screenEncodeTimeSeconds: null,
      screenCodec: null,
      screenLimitation: 'none',
      screenScale: null,
      path: 'unknown',
    }

    let received = 0
    let lost = 0
    let screenReason: LimitationReason | null = null

    // Two video senders may be live at once — the camera and a share — and
    // two video receivers likewise. The mid on each report says which.
    const cameraMid = this.base.video.mid
    const screenMid = this.screenTransceivers.get('screen')?.mid ?? null

    const report = await this.pc.getStats()
    // Reports refer to each other by id (a pair to its candidates, a stream
    // to its codec); index them once.
    const byId = new Map<string, Record<string, unknown>>()
    report.forEach((s) => byId.set(String((s as { id: string }).id), s as Record<string, unknown>))
    const candidate = (id: unknown): CandidateLike | undefined =>
      typeof id === 'string' ? (byId.get(id) as CandidateLike | undefined) : undefined

    report.forEach((s) => {
      if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.nominated) {
        if (typeof s.currentRoundTripTime === 'number') {
          out.roundTripTimeMs = Math.round(s.currentRoundTripTime * 1000)
        }
        out.path = describePath(candidate(s.localCandidateId), candidate(s.remoteCandidateId))
      }
      if (s.type === 'outbound-rtp' && s.kind === 'video') {
        const mid: string | undefined = s.mid
        if (screenMid !== null && mid === screenMid) {
          out.screenSentWidth = s.frameWidth ?? null
          out.screenSentHeight = s.frameHeight ?? null
          out.screenSentFramesPerSecond = s.framesPerSecond ?? null
          out.screenBytesSent = s.bytesSent ?? null
          out.screenFramesEncoded = s.framesEncoded ?? null
          out.screenEncodeTimeSeconds = s.totalEncodeTime ?? null
          const codec =
            typeof s.codecId === 'string'
              ? (byId.get(s.codecId) as { mimeType?: string } | undefined)
              : undefined
          out.screenCodec = codec?.mimeType ?? null
          const reason = s.qualityLimitationReason
          const verdict: LimitationReason =
            reason === 'cpu' || reason === 'bandwidth' || reason === 'other' ? reason : 'none'
          screenReason = verdict
          out.screenLimitation = verdict
          return
        }
        if (mid !== undefined && cameraMid !== null && mid !== cameraMid) return
        const reason = s.qualityLimitationReason
        if (reason === 'cpu' || reason === 'bandwidth' || reason === 'other') {
          out.qualityLimitationReason = reason
        }
        out.sentWidth = s.frameWidth ?? null
        out.sentHeight = s.frameHeight ?? null
        out.sentFramesPerSecond = s.framesPerSecond ?? null
      }
      if (s.type === 'inbound-rtp' && !s.isRemote) {
        received += s.packetsReceived ?? 0
        lost += s.packetsLost ?? 0
        if (typeof s.jitter === 'number') out.jitterMs = Math.round(s.jitter * 1000)
        // The camera's picture rates the link; a share's frame rate says
        // nothing about the network (a still slide is a still slide).
        const mid: string | undefined = s.mid
        const inboundKind = mid === undefined ? 'camera' : (this.remoteKinds.get(mid) ?? 'camera')
        if (s.kind === 'video' && inboundKind === 'camera') {
          out.framesPerSecond = s.framesPerSecond ?? null
          out.frameWidth = s.frameWidth ?? null
          out.frameHeight = s.frameHeight ?? null
          out.freezeCount = s.freezeCount ?? 0
        }
      }
    })

    const total = received + lost
    out.packetsReceived = received
    out.packetsLost = lost
    out.packetsLostRatio = total > 0 ? lost / total : 0

    // The share's encoder said how it is doing toward this peer; the
    // adapter decides whether to step the resolution down or back up.
    if (screenReason !== null && this.screenProfile) {
      if (this.screenAdapter.observe(screenReason)) void this.applyScreenProfile()
      out.screenScale = this.screenProfile.scaleResolutionDownBy * this.screenAdapter.scale
    }
    return out
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    this.pc.onnegotiationneeded = null
    this.pc.onicecandidate = null
    this.pc.ontrack = null
    this.pc.onconnectionstatechange = null
    this.senders.clear()
    this.screenTransceivers.clear()
    this.remoteKinds.clear()
    this.pc.close()
  }
}

/**
 * Offer VP9 first for a share. setCodecPreferences must run before the
 * offer that carries the transceiver, which is why this is called the
 * moment it is created. Browsers without the method, or with a capability
 * list that does not satisfy it, keep their default order.
 */
function preferCodecs(transceiver: RTCRtpTransceiver): void {
  try {
    const caps = RTCRtpReceiver.getCapabilities?.('video')
    if (!caps || typeof transceiver.setCodecPreferences !== 'function') return
    transceiver.setCodecPreferences(preferScreenCodecs(caps.codecs))
  } catch {
    // The default order, then; VP8 still works, only bigger.
  }
}

/**
 * Ask the receiver for no extra playout delay on a share: text wants to
 * arrive, not to be smooth. Only a floor is being lowered here — the
 * browser's own jitter estimate still applies — under whichever name the
 * browser gives it.
 */
function preferLowDelay(receiver: RTCRtpReceiver): void {
  const r = receiver as unknown as Record<string, unknown>
  try {
    if ('jitterBufferTarget' in r) r.jitterBufferTarget = 0
    else if ('playoutDelayHint' in r) r.playoutDelayHint = 0
  } catch {
    // Not adjustable here; the default is already low.
  }
}

/** Stopped, by us or by the connection closing. `stopped` is the older name some browsers still expose. */
function stopped(transceiver: RTCRtpTransceiver): boolean {
  return (
    transceiver.currentDirection === 'stopped' ||
    (transceiver as RTCRtpTransceiver & { stopped?: boolean }).stopped === true
  )
}
